import { useState, type FormEvent } from 'react'
import { Alert, Box, Button, Card, CardContent, Link as MuiLink, Stack, TextField, Typography } from '@mui/material'
import LockRounded from '@mui/icons-material/LockRounded'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { TurnstileWidget } from '../components/TurnstileWidget'
import { BrandBackground } from '../components/BrandBackground'
import { PublicHeader } from '../components/PublicHeader'
import { AppFooter } from '../components/AppFooter'

// Mesmo azul do site institucional / da tela de contratação.
const BRAND = '#2563eb'
const BRAND_ACCENT = '#0ea5e9'

// Sem site key (dev local) o TurnstileWidget não renderiza nada e o backend também não
// exige o token -- o login segue funcionando sem CAPTCHA. Em produção a key vem embutida
// no build (VITE_TURNSTILE_SITE_KEY, ver compose.production.yaml).
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY

export function LoginPage() {
  const auth = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [captchaAttempt, setCaptchaAttempt] = useState(0)

  if (auth.authenticated) return <Navigate to="/" replace />

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await auth.login(username, password, turnstileToken)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível autenticar.')
      // Token do Turnstile é de uso único -- força um novo para a próxima tentativa.
      setCaptchaAttempt((n) => n + 1)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <BrandBackground />
      <PublicHeader action={<Button href="/contratar" size="small">Criar conta</Button>} />
      <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 440, boxShadow: '0 28px 64px -24px rgba(15,23,42,.28), 0 0 0 1px rgba(15,23,42,.04)' }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Stack alignItems="center" textAlign="center">
            <Stack sx={{ width: 100, height: 100, borderRadius: 4, display: 'grid', placeItems: 'center', color: 'white', background: `linear-gradient(135deg,${BRAND},${BRAND_ACCENT})`, boxShadow: '0 16px 34px -14px rgba(37,99,235,.55)', mb: 2 }}>
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none">
                <ellipse cx="12" cy="12" rx="9" ry="3.6" transform="rotate(-25 12 12)" stroke="currentColor" strokeWidth="1.6" />
                <circle cx="12" cy="12" r="3" fill="currentColor" />
              </svg>
            </Stack>
            <Typography variant="h3">Órbita</Typography>
            <Typography color="text.secondary" mt={1} mb={4}>Acesse o painel seguro da sua organização.</Typography>
          </Stack>
          <Stack component="form" onSubmit={handleSubmit} gap={2}>
            <TextField label="Usuário ou e-mail" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoFocus required fullWidth />
            <TextField label="Senha" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required fullWidth />
            {TURNSTILE_SITE_KEY && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <TurnstileWidget onToken={setTurnstileToken} resetKey={captchaAttempt} />
              </Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            <Button type="submit" fullWidth size="large" variant="contained" startIcon={<LockRounded />} loading={submitting}
              disabled={Boolean(TURNSTILE_SITE_KEY) && !turnstileToken}
              sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#1d4ed8' } }}>
              Entrar
            </Button>
            <Typography variant="body2" textAlign="center">
              <MuiLink component={Link} to="/esqueci-senha" sx={{ color: BRAND, fontWeight: 600 }} underline="hover">
                Esqueceu sua senha?
              </MuiLink>
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={3}>
            Autenticação protegida por OAuth 2.0 / OpenID Connect
          </Typography>
        </CardContent>
      </Card>
      </Box>
      <AppFooter />
    </Box>
  )
}
